import { DateTime } from "luxon";

import { SpendTransactionsRequest_Filters } from "lib/generated/app-service-gql/graphql";
import { SpendBuyerTypeTimeUnit, SpendTimeUnit } from "lib/types/graphQLEnums";

/**
 * Shared filter predicates across all spend queries/mutations (transactions,
 * top buyers/suppliers, spend-over-time, export). The schema reuses one input
 * type for every spend operation, exposed here under a neutral name.
 */
export type SpendFilters = SpendTransactionsRequest_Filters;

/**
 * Whether a spend query is narrow enough to run without a date range.
 * Supplier guids go through an index and take their total from the rollup, so
 * how many there are does not matter. A file id is one upload bounded by its
 * own index, though it counts on the raw table, since the rollup aggregates the
 * file away. A name does neither and a buyer alone cannot page selectively:
 * both need a date range.
 */
export function isQueryableSpendFilter(filters: SpendFilters): boolean {
  if (filters.dateFrom || filters.dateTo) {
    return true;
  }
  if (filters.supplierNames?.length || filters.supplierNamesExact?.length) {
    return false;
  }

  return Boolean(filters.fileId) || Boolean(filters.supplierGuids?.length);
}

type SupplierSpendFilterArgs = {
  buyerGuid: string;
  supplierGuid?: string;
  /** Only used when no guid is available: a fuzzy full-text match on the source name. */
  supplierName?: string;
};

/**
 * A buyer's spend with one supplier.
 *
 * Prefers the guid: a name is matched as a conjunction of its words, so a precise
 * organisation name misses rows recorded under a shorter one, and a vague name
 * pulls in unrelated organisations that happen to share it.
 */
export function supplierSpendFilter({
  buyerGuid,
  supplierGuid,
  supplierName,
}: SupplierSpendFilterArgs): SpendFilters {
  return {
    buyerGuids: [buyerGuid],
    supplierGuids: supplierGuid ? [supplierGuid] : undefined,
    supplierNames: !supplierGuid && supplierName ? [supplierName] : undefined,
  };
}

/**
 * Widens the date range out to whole periods: `dateFrom` back to the start of its
 * period, `dateTo` on to the end of its. A range that starts mid-quarter would
 * otherwise make the first bucket a part-period, which charts as a dip that isn't
 * in the data. Either bound can be missing, and a missing one stays missing.
 */
export function snapToWholePeriods(
  filters: SpendFilters,
  dateUnit: "month" | "quarter" | "year",
): SpendFilters {
  const snap = (date: string | null | undefined, edge: "startOf" | "endOf") => {
    if (!date) {
      return date;
    }
    const parsed = DateTime.fromISO(date);
    return parsed.isValid ? parsed[edge](dateUnit).toISODate() ?? date : date;
  };

  return {
    ...filters,
    dateFrom: snap(filters.dateFrom, "startOf"),
    dateTo: snap(filters.dateTo, "endOf"),
  };
}

/** UI date-unit (used by the chart date selector) → GraphQL `SpendTimeUnit`. */
export const SPEND_TIME_UNIT: Record<"month" | "quarter" | "year", SpendTimeUnit> = {
  month: SpendTimeUnit.Month,
  quarter: SpendTimeUnit.Quarter,
  year: SpendTimeUnit.Year,
};

/** The same buckets for the buyer type query, which has its own enum for them. */
export const SPEND_BUYER_TYPE_TIME_UNIT: Record<
  "month" | "quarter" | "year",
  SpendBuyerTypeTimeUnit
> = {
  month: SpendBuyerTypeTimeUnit.Month,
  quarter: SpendBuyerTypeTimeUnit.Quarter,
  year: SpendBuyerTypeTimeUnit.Year,
};
